import { authenticatedFetch, getTokenOrThrow } from './api';
import type { Session, CoachSessionDetail, CoachMessage } from '../types';

export interface CoachRequest {
  session_id: string;
  prompt: string;
}

export interface CoachResponse {
  agent_response: string;
  session_id: string;
  usage_remaining: number | null;
}

const API_BASE = import.meta.env.VITE_API_URL;
const STREAM_URL = import.meta.env['VITE_COACH_STREAM_URL'];

export async function sendCoachMessage(
  request: CoachRequest
): Promise<CoachResponse> {
  const token = await getTokenOrThrow();
  const response = await authenticatedFetch(
    `${API_BASE}/v1/coach/message`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
    token
  );

  if (response.status === 429) {
    throw new Error('LIMIT_REACHED');
  }

  if (!response.ok) {
    throw new Error('COACH_ERROR');
  }

  return response.json();
}

export async function streamCoachMessage(
  request: CoachRequest,
  onChunk: (text: string) => void,
  onDone: (usageRemaining: number | null) => void,
  onError: (error: string) => void,
): Promise<void> {
  const token = await getTokenOrThrow();

  const streamEndpoint = STREAM_URL?.endsWith('/')
    ? `${STREAM_URL}v1/coach/stream`
    : `${STREAM_URL}/v1/coach/stream`;

  let response: Response;
  try {
    response = await fetch(streamEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });
  } catch {
    onError('COACH_ERROR');
    return;
  }

  if (response.status === 429) {
    onError('LIMIT_REACHED');
    return;
  }
  if (response.status === 400) {
    let detail = 'Your input was flagged. Please revise and try again.';
    try {
      const body = await response.json();
      if (body?.detail && typeof body.detail === 'string') detail = body.detail;
    } catch { /* ignore parse failures */ }
    onChunk(detail);
    onDone(null);
    return;
  }
  if (!response.ok) {
    onError('COACH_ERROR');
    return;
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let usageRemaining: number | null = null;

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') {
          onDone(usageRemaining);
          return;
        }
        try {
          const parsed = JSON.parse(data);
          if (typeof parsed === 'string') {
            onChunk(parsed);
          } else if (
            typeof parsed === 'object' &&
            parsed !== null &&
            'usage_remaining' in parsed
          ) {
            usageRemaining = parsed.usage_remaining;
          } else if (
            typeof parsed === 'object' &&
            parsed !== null &&
            'error' in parsed
          ) {
            onError('COACH_ERROR');
            return;
          }
        } catch {
          /* skip malformed */
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
  onDone(usageRemaining);
}

/**
 * List the user's recent coach sessions. Returns [] on any error —
 * the sessions panel is non-critical and must never block the chat.
 */
export async function listCoachSessions(): Promise<Session[]> {
  const token = await getTokenOrThrow();
  const res = await authenticatedFetch(
    `${API_BASE}/v1/coach/sessions`,
    { headers: {} },
    token
  );
  if (!res.ok) return [];
  return res.json();
}

/** Fetch one full session (including messages) by id. */
export async function getCoachSession(
  sessionId: string
): Promise<CoachSessionDetail> {
  const token = await getTokenOrThrow();
  const res = await authenticatedFetch(
    `${API_BASE}/v1/coach/sessions/${sessionId}`,
    { headers: {} },
    token
  );
  if (!res.ok) throw new Error('Session not found');
  return res.json();
}

/** Persist a session's transcript. Fire-and-forget — failures are ignored. */
export async function saveCoachSession(
  sessionId: string,
  messages: CoachMessage[]
): Promise<void> {
  const token = await getTokenOrThrow();
  const normalized = messages.map(m =>
    m.role === 'coach' ? { ...m, role: 'assistant' } : m
  );
  await authenticatedFetch(
    `${API_BASE}/v1/coach/sessions`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, messages: normalized }),
    },
    token
  );
}
